import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { ArrowUpToLine, Loader2, Plus, Trash2 } from 'lucide-react';
import { ReactComponent as Publicar } from '../../assets/svg/publicarProyecto.svg';
import styles from './NewPrivateProjectV3.module.scss';
import { useNewProjectManager } from '../../hooks/newProject/useNewProjectManager.ts';
import { useNewProjectCreation } from '../../hooks/newProject/useNewProjectCreation.ts';
import NewProjectFormV2 from '../../components/UI/NewProjectForm/NewProjectFormV2.tsx';
import NewSubProjectManagerV2 from '../../components/UI/NewSubProjectManager/NewSubProjectManagerV2.tsx';
import NewSubProjectModalV2 from '../../components/UI/NewSubProjectModal/NewSubProjectModalV2.tsx';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';
import ManualAssistedProjectPrecarga, {
  type ManualPrecargaPhase
} from './ManualAssistedProjectPrecarga/ManualAssistedProjectPrecarga.tsx';
import {
  openMainModal,
  closeMainModal,
  resetSubProjects,
  clearPostulationProject,
  resetIdProject,
  resetProjectToEdit,
  setProjectToEdit,
} from '../../store/actions';
import { useActionGuard } from '../../hooks/useActionGuard.ts';
import { fireAnalytics } from '../../utils/firebase';
import { getSessionId, getDeviceCategory } from '../../utils/analyticsHelpers.ts';

interface NewPrivateProjectV3Props {
  openMainModal: (config: any) => void;
  closeMainModal: () => void;
  postulationProject: any;
  updateProyecto: (project: any, images: any, projectId: number, navigate: any) => void;
  createNewProject: (project: any, subProjects: any, navigate: any) => void;
  resetSubProjects: () => void;
  clearPostulationProject: () => void;
  resetIdProject: () => void;
  resetProjectToEdit: () => void;
  setProjectToEdit: (id: number) => void;
  userData: any;
  edifySpinner: boolean;
  subProjects: any[];
  step1Project: any;
}

const NewPrivateProjectV3: React.FC<NewPrivateProjectV3Props> = ({
  openMainModal,
  closeMainModal,
  postulationProject,
  updateProyecto,
  createNewProject,
  resetSubProjects,
  clearPostulationProject,
  resetIdProject,
  resetProjectToEdit,
  setProjectToEdit,
  userData,
  edifySpinner,
  subProjects,
  step1Project
}) => {
  const location = useLocation();
  const { id: routeProjectId } = useParams<{ id?: string }>();
  const isEditing = location?.pathname?.includes("edit");
  const projectId = isEditing && routeProjectId ? routeProjectId : undefined;

  // Hooks del nuevo sistema
  const { state, actions, isValid, canPublish, hasPendingChanges } = useNewProjectManager(projectId);
  const { createProjectWithSubProjects, deleteProjectWithFlow, getCreationValidationErrors } = useNewProjectCreation();
  const { guard } = useActionGuard();
  // Estado local para UI
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const [isLoading, setIsLoading] = useState(false);
  const [isInWelcomeState, setIsInWelcomeState] = useState(true);
  const [lastAddedSubProjectId, setLastAddedSubProjectId] = useState<string | null>(null);
  const [assistedPrecargaActive, setAssistedPrecargaActive] = useState(false);
  const [assistedPrecargaSeedFile, setAssistedPrecargaSeedFile] = useState<File | null>(null);
  const [assistedPrecargaResumeDraftId, setAssistedPrecargaResumeDraftId] = useState<string | null>(null);
  const [precargaUiPhase, setPrecargaUiPhase] = useState<ManualPrecargaPhase>('pick');

  useEffect(() => {
    if (isEditing) {
      setAssistedPrecargaActive(false);
      setAssistedPrecargaResumeDraftId(null);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!assistedPrecargaActive) {
      setPrecargaUiPhase('pick');
      setAssistedPrecargaSeedFile(null);
    }
  }, [assistedPrecargaActive]);

  const clearAssistedPrecargaSeedFile = useCallback(() => {
    setAssistedPrecargaSeedFile(null);
  }, []);

  // Inicializar estados
  useEffect(() => {
    resetProjectToEdit();
    resetSubProjects();
    resetIdProject();
    clearPostulationProject();
  }, [resetProjectToEdit, resetSubProjects, resetIdProject, clearPostulationProject]);

  useEffect(() => {
    if (isEditing && projectId) {
      const numericId = parseInt(projectId, 10);
      if (!Number.isNaN(numericId)) {
        setProjectToEdit(numericId);
      }
    }
  }, [isEditing, projectId, setProjectToEdit]);

  useEffect(() => {
    if (!lastAddedSubProjectId) return;
    const t = setTimeout(() => setLastAddedSubProjectId(null), 3000);
    return () => clearTimeout(t);
  }, [lastAddedSubProjectId]);

  // Crear nuevo proyecto
  const handleCreateProject = guard("create_project", async () => {
    if (!canPublish) {
      const errors = getCreationValidationErrors(
        state.projectData,
        state.subProjects,
        state.projectImages
      );
      
      openMainModal({
        title: "¡Atención!",
        body: errors.join('\n'),
        isSuccess: false,
      });
      return;
    }

    setIsLoading(true);
    try {
      await createProjectWithSubProjects(state.projectData, state.subProjects, state.projectImages);
    } catch (error) {
      console.error('Error al crear proyecto:', error);
      openMainModal({
        title: "Error",
        iconType: "error",
        body: "No se pudo crear el proyecto. Intenta nuevamente.",
        isSuccess: true,
        confirmHandler: null,
      });
    } finally {
      setIsLoading(false);
    }
  });

  // Actualizar proyecto
  const handleUpdateProject = async () => {
    if (!isValid) {
      const missing = getMissingFields();
      let bodyMessage = "Por favor completa todos los campos requeridos";
      
      // Mensaje específico si no hay rubros
      if (state.subProjects.length === 0) {
        bodyMessage = "No puedes guardar el proyecto sin al menos 1 rubro para cotizar. Agrega un rubro o elimina el proyecto completo.";
      } else if (missing.length > 0) {
        bodyMessage = `Faltan completar: ${missing.join(", ")}`;
      }
      
      openMainModal({
        title: "¡Atención!",
        body: bodyMessage,
        iconType: "warning",
        isSuccess: true,
        confirmHandler: null,
      });
      return;
    }

    if (!hasPendingChanges) {
      openMainModal({
        title: "Sin cambios",
        body: "No hay cambios pendientes para guardar",
        isSuccess: true,
        confirmHandler: null,
      });
      return;
    }

    openMainModal({
      title: "¿Deseas guardar los cambios efectuados?",
      body: "Si tenías usuarios postulados en el proyecto, se le notificarán de los cambios realizados.",
      iconType: "warning",
      isSuccess: false,
      confirmHandler: async () => {
        // Cerrar modal inmediatamente para mejor UX
        closeMainModal();
        
        try {
          setIsLoading(true);
          await actions.saveAllChanges();
        } catch (error) {
          console.error('Error al guardar cambios:', error);
          openMainModal({
            title: "Error",
            body: "Hubo un error al guardar los cambios. Por favor, inténtalo de nuevo.",
            iconType: "error",
            isSuccess: true,
            confirmHandler: null,
          });
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const handleDeleteProject = () => {
    const rawId = postulationProject?.id ?? projectId;
    if (!rawId) {
      return;
    }
    const numericId = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
    if (Number.isNaN(numericId)) {
      return;
    }
    deleteProjectWithFlow(numericId);
  };

  const handleSaveSubProject = (subProject: any) => {
    if (subProject.isNew) {
      const newId = actions.addSubProject(subProject);
      setLastAddedSubProjectId(newId);
    } else {
      actions.updateSubProject(subProject.id, subProject);
    }
    actions.closeSubProjectModal();
  };

  // Obtener qué falta por completar
  const getMissingFields = () => {
    const missing = [];
    
    if (!state.projectData.name) missing.push("nombre del proyecto");
    if (!state.projectData.description) missing.push("descripción");
    if (!state.projectData.startDate) missing.push("fecha de inicio");
    if (!state.projectData.endDate) missing.push("fecha de finalización");
    if (!state.projectData.zone) missing.push("ubicación");
    if (state.subProjects.length === 0) missing.push("al menos un rubro");
    
    return missing;
  };

  // Verificar si el proyecto no tiene subproyectos en modo edición
  const hasNoSubProjects = isEditing && state.subProjects.length === 0;

  const getButtonText = () => {
    if (isEditing) {
      if (hasNoSubProjects) {
        return "Eliminar proyecto (sin rubros)";
      }
      return "Guardar cambios";
    }
    if (canPublish) {
      return "Publicar proyecto";
    }
    const missing = getMissingFields();
    if (state.subProjects.length === 0) {
      return "Agregar al menos 1 rubro";
    }
    if (isInWelcomeState && !canPublish) {
      return "Hacé clic en Comenzar para cargar los datos del proyecto";
    }
    if (missing.length === 1) {
      return `Falta: ${missing[0]}`;
    }
    if (missing.length > 1) {
      return `Faltan: ${missing.slice(0, 2).join(", ")}${missing.length > 2 ? "..." : ""}`;
    }
    return "Completá los campos";
  };

  const showPrecargaDraftEditor =
    assistedPrecargaActive && !isEditing && precargaUiPhase === 'editor';

  return (
    <div className={styles.container}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      
      <div className={styles.wrapper}>
        <motion.div
          className={styles.projectContainer}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className={styles.breadcrumbBar}>
            <div className={styles.breadcrumbContainer}>
              <Breadcrumbs />
            </div>
            {!isEditing && (assistedPrecargaActive || assistedPrecargaResumeDraftId != null) ? (
              <div className={styles.precargaBackSlot}>
                {assistedPrecargaActive ? (
                  <button
                    type="button"
                    className={styles.precargaBackLink}
                    onClick={() => setAssistedPrecargaActive(false)}
                  >
                    Volver al inicio
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.precargaBackLink}
                    onClick={() => {
                      fireAnalytics('new_project_breadcrumb_resume_assisted_precarga_click', {
                        category: 'private_projects',
                        action: 'breadcrumb_resume_assisted_precarga',
                        session_id: getSessionId(),
                        device_category: getDeviceCategory()
                      });
                      setAssistedPrecargaSeedFile(null);
                      setAssistedPrecargaActive(true);
                    }}
                  >
                    Continuar precarga
                  </button>
                )}
              </div>
            ) : null}
          </div>
          <main
            className={
              showPrecargaDraftEditor
                ? `${styles.main} ${styles.mainPrecargaDraftEditor}`
                : styles.main
            }
          >
            <div
              className={
                showPrecargaDraftEditor
                  ? `${styles.bodyColumn} ${styles.bodyColumnPrecargaEditor}`
                  : styles.bodyColumn
              }
            >
              {assistedPrecargaActive && !isEditing ? (
                <>
                  <section className={styles.section}>
                    <ManualAssistedProjectPrecarga
                      variant="embedded"
                      initialFile={assistedPrecargaSeedFile}
                      onInitialFileConsumed={clearAssistedPrecargaSeedFile}
                      onBackToClassic={() => setAssistedPrecargaActive(false)}
                      onPhaseChange={setPrecargaUiPhase}
                      resumeDraftId={assistedPrecargaResumeDraftId}
                      onPrecargaDraftIdChange={setAssistedPrecargaResumeDraftId}
                    />
                  </section>
                </>
              ) : (
                <>
                  <section className={styles.section}>
                    <NewProjectFormV2
                      projectData={state.projectData}
                      projectImages={state.projectImages}
                      validation={state.validation}
                      onProjectDataChange={actions.updateProjectData}
                      onImagesChange={actions.setProjectImages}
                      isEditing={isEditing}
                      isLoading={isLoading}
                      onWelcomeStateChange={setIsInWelcomeState}
                      onChooseAssistedPrecarga={
                        isEditing
                          ? undefined
                          : (file) => {
                              setAssistedPrecargaResumeDraftId(null);
                              setAssistedPrecargaSeedFile(file);
                              setAssistedPrecargaActive(true);
                            }
                      }
                    />
                  </section>
                </>
              )}
            </div>
            {assistedPrecargaActive && !isEditing ? (
              precargaUiPhase !== 'editor' ? (
                <aside className={styles.summaryColumn}>
                  <div className={styles.summaryPanel}>
                    <h3 className={styles.summaryTitle}>Rubros</h3>
                    {precargaUiPhase === 'processing' ? (
                      <p className={styles.summaryProcessingRow}>
                        <Loader2 size={18} className={styles.summaryProcessingSpinner} aria-hidden />
                        Analizando el documento… Los rubros sugeridos aparecerán al finalizar.
                      </p>
                    ) : (
                      <p className={styles.summaryEmpty}>
                        Elegí un PDF desde el selector de archivos. Si lo cerraste, usá «Elegir PDF» en la columna
                        principal. Para cargar rubros a mano, volvé al inicio y tocá Comenzar.
                      </p>
                    )}
                  </div>
                </aside>
              ) : null
            ) : (
              <aside className={styles.summaryColumn}>
                <NewSubProjectManagerV2
                  subProjects={state.subProjects}
                  validation={state.validation}
                  onAddSubProject={actions.addSubProject}
                  onUpdateSubProject={actions.updateSubProject}
                  onRemoveSubProject={actions.removeSubProject}
                  onOpenSubProjectModal={actions.openSubProjectModal}
                  isEditing={isEditing}
                  isLoading={isLoading}
                  projectStartDate={state.projectData.startDate}
                  projectEndDate={state.projectData.endDate}
                  lastAddedSubProjectId={lastAddedSubProjectId}
                  primaryAction={
                    state.subProjects.length === 0 && !isEditing ? (
                      <button
                        type="button"
                        className={`${styles.button} ${styles.primaryButton}`}
                        onClick={() => {
                          fireAnalytics("new_project_rubros_empty_state_cta_click", {
                            category: "private_projects",
                            action: "empty_state_add_rubro",
                            session_id: getSessionId(),
                            device_category: getDeviceCategory(),
                            ...(userData?.id && { edify_user_id: userData.id }),
                          });
                          actions.openSubProjectModal('new');
                        }}
                        disabled={isLoading}
                      >
                        <div className={styles.buttonContent}>
                          {isLoading ? (
                            <Loader2 size={18} className={styles.spinner} />
                          ) : (
                            <Plus size={20} />
                          )}
                          {isLoading ? 'Procesando...' : 'Agregar al menos 1 rubro'}
                        </div>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`${styles.button} ${hasNoSubProjects ? styles.deleteButton : styles.primaryButton} ${(!canPublish && !isEditing) ? styles.disabled : ''} ${isEditing && hasPendingChanges ? styles.hasChanges : ''}`}
                        onClick={isEditing ? handleUpdateProject : handleCreateProject}
                        disabled={(!canPublish && !isEditing) || isLoading || (isEditing && !hasPendingChanges)}
                      >
                        <div className={styles.buttonContent}>
                          {isLoading ? (
                            <Loader2 size={18} className={styles.spinner} />
                          ) : (
                            hasNoSubProjects ? <Trash2 size={18} /> : (isEditing ? <Publicar /> : <ArrowUpToLine size={24} />)
                          )}
                          {isLoading ? 'Procesando...' : getButtonText()}
                          {isEditing && hasPendingChanges && !hasNoSubProjects && !isLoading && (
                            <span className={styles.pendingIndicator}>•</span>
                          )}
                        </div>
                      </button>
                    )
                  }
                  managerFooter={
                    isEditing ? (
                      <button
                        type="button"
                        className={`${styles.button} ${styles.deleteButton}`}
                        onClick={handleDeleteProject}
                        disabled={isLoading}
                      >
                        <Trash2 size={18} />
                        Eliminar proyecto
                      </button>
                    ) : null
                  }
                />
              </aside>
            )}
          </main>
        </motion.div>
      </div>

      <NewSubProjectModalV2
        isOpen={state.ui.isSubProjectModalOpen}
        subProject={state.subProjects.find(sub => sub.id === state.ui.editingSubProjectId) || null}
        onClose={actions.closeSubProjectModal}
        onSave={handleSaveSubProject}
        projectStartDate={state.projectData.startDate}
        projectEndDate={state.projectData.endDate}
        isLoading={isLoading}
        isCreatingNew={state.ui.editingSubProjectId === 'new'}
        existingSubProjects={state.subProjects}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.general.userData,
    projectIdForSub: state.profile.projectIdForSub,
    step1Project: state.profile.projectStep1Id,
    edifySpinner: state.general.edifySpinner,
    subProjects: state.profile.sendSubprojects,
    postulationProject: state.dashboard.postulationProject,
  };
};

export default connect(mapStateToProps, {
  resetIdProject,
  openMainModal,
  closeMainModal,
  clearPostulationProject,
  resetSubProjects,
  resetProjectToEdit,
  setProjectToEdit,
})(NewPrivateProjectV3);
